import {SettingParam} from "@/types/node";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {useVariables, Variable} from "@/contexts/VariablesContext";
import {useSubflows} from "@/contexts/SubflowsContext";
import {cn} from "@/lib/utils";
import {Tooltip, TooltipContent, TooltipTrigger} from "../ui/tooltip";
import {HelpCircle} from "lucide-react";

export const SettingVariableSelect = ({
  param,
  handleChange,
  placeholder,
  disabled = false,
  className,
  tooltipMessage,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: Variable) => void;
  placeholder?: string;
  selectedNodeId: string;
  disabled: boolean;
  className?: string;
  tooltipMessage?: string;
}) => {
  const {getSelectedSubflowVariables} = useVariables();
  const {selectedSubflowId} = useSubflows();
  const variables = getSelectedSubflowVariables(selectedSubflowId || "main");

  const handleValueChange = (value: string) => {
    const variable = variables.find(v => v.name === value);
    if (variable) {
      handleChange(param, variable);
    }
  };

  return (
    <div className="mb-4">
      {param.label && (
        <label className="text-sm font-medium text-label mb-2 flex items-center gap-2">
          {param.label}
          {tooltipMessage && (
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger>
                <HelpCircle className="w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent
                side="top"
                sideOffset={10}
                className="max-w-72 text-center"
              >
                {tooltipMessage}
              </TooltipContent>
            </Tooltip>
          )}
        </label>
      )}
      <Select
        onValueChange={value => handleValueChange(value)}
        value={param.value?.toString() || ""}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            disabled ? "pointer-events-none" : "",
            className,
          )}
        >
          <SelectValue placeholder={placeholder || "Select branch or step"} />
        </SelectTrigger>
        <SelectContent className="max-w-[398px]">
          {variables.length > 0 ? (
            variables.map(variable => (
              <SelectItem value={variable.name} key={variable.name}>
                {variable.name}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="No variables" disabled>
              No variables found
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
